import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "../styles/encuesta4c.module.css";

/**
 * Desplegable custom alineado con la estética del formulario freemium.
 * options: [{ value, label }]
 */
export default function FormSelect({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  searchable = false,
}) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder || t("common.chooseOption");
  const generatedId = useId();
  const fieldId = id || generatedId;
  const listId = `${fieldId}-list`;
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = options.find((o) => o.value === value);
  const filtered = searchable
    ? options.filter((o) =>
        o.label.toLowerCase().includes(query.trim().toLowerCase())
      )
    : options;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && searchable) {
      searchRef.current?.focus();
    }
  }, [open, searchable]);

  const toggle = () => {
    if (disabled) return;
    setOpen((v) => !v);
    setQuery("");
  };

  const pick = (next) => {
    onChange(next);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className={styles.formGroup} ref={rootRef}>
      {label ? (
        <label className={styles.formLabel} htmlFor={fieldId} id={`${fieldId}-label`}>
          {label}
        </label>
      ) : null}

      <div className={styles.formSelectWrap}>
        <button
          type="button"
          id={fieldId}
          className={`${styles.formSelectTrigger} ${
            open ? styles.formSelectTriggerOpen : ""
          } ${!selected ? styles.formSelectPlaceholder : ""}`}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          aria-labelledby={label ? `${fieldId}-label` : undefined}
          disabled={disabled}
          onClick={toggle}
        >
          <span className={styles.formSelectValue}>
            {selected ? selected.label : resolvedPlaceholder}
          </span>
          <span className={styles.formSelectChevron} aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 6l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>

        {open && (
          <div className={styles.formSelectPanel} role="presentation">
            {searchable && (
              <div className={styles.formSelectSearch}>
                <input
                  ref={searchRef}
                  type="search"
                  className={styles.formSelectSearchInput}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("common.search")}
                  autoComplete="off"
                />
              </div>
            )}
            <ul
              id={listId}
              className={styles.formSelectList}
              role="listbox"
              aria-labelledby={label ? `${fieldId}-label` : undefined}
            >
              {filtered.length === 0 && (
                <li className={styles.formSelectEmpty}>{t("common.noResults")}</li>
              )}
              {filtered.map((o) => {
                const isSelected = o.value === value;
                return (
                  <li key={o.value} role="option" aria-selected={isSelected}>
                    <button
                      type="button"
                      className={`${styles.formSelectOption} ${
                        isSelected ? styles.formSelectOptionSelected : ""
                      }`}
                      onClick={() => pick(o.value)}
                    >
                      <span>{o.label}</span>
                      {isSelected && (
                        <span className={styles.formSelectCheck} aria-hidden="true">
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path
                              d="M2.5 7.2L5.4 10.1L11.5 3.9"
                              stroke="currentColor"
                              strokeWidth="1.75"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
